import React, { useCallback, useMemo } from 'react';
import { Row, Col, Tooltip, Button } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { bytesFormat } from '@utils/formatters';
import { useTranslation } from 'react-i18next';
import CustomCard from '@components/CustomCard';

import * as S from './styles';

interface ConsumptionCardProps {
  data: {
    updatedDate?: string;
    totalFranchise: number;
    franchiseConsumption: number;
    surplusConsumption: number;
    totalConsumption: number;
  };
  currentConsumption?: {
    consumption: number;
    franchise: number;
  };
}

const ConsumptionCard = ({
  data,
  currentConsumption,
}: ConsumptionCardProps) => {
  const { t } = useTranslation('contracts');

  const hasCurrentConsumption = useMemo(
    () => !!currentConsumption,
    [currentConsumption],
  );

  const updatedDate = useMemo(() => {
    const date = data?.updatedDate;
    if (!date) return undefined;

    const [day, month, year, hour, minute, second] = date.split('-');

    const formattedDate = `${day}/${month}/${year} ${hour}:${minute}:${second}`;

    return formattedDate;
  }, [data]);

  const rateConsumptionStates = useMemo(() => {
    const {
      totalConsumption,
      totalFranchise,
      franchiseConsumption,
      surplusConsumption,
    } = data;

    return [
      {
        name: 'totalConsumption',
        title: `${t('totalConsumption')} / ${t('totalFranchise')}`,
        value: `${bytesFormat(totalConsumption)} / ${bytesFormat(
          totalFranchise,
        )}`,
        md: 10,
      },
      {
        name: 'franchiseConsumption',
        value: bytesFormat(franchiseConsumption),
        md: 7,
      },
      {
        name: 'surplusConsumption',
        value: bytesFormat(surplusConsumption),
        md: 7,
      },
    ];
  }, [data, t]);

  const statsInfo = useCallback(
    (title: string) => (
      <Tooltip title={t(title)}>
        <Button type="link" icon={<QuestionCircleOutlined />} />
      </Tooltip>
    ),
    [t],
  );

  const progressBar = useCallback(
    (value?: number, total?: number, size?: number) => {
      if (!value || !total) {
        return <></>;
      }

      const percentage = (value / total) * 100;

      return (
        <S.ProgressBarContainer size={size}>
          <S.ProgressBarPercentage>{`${percentage.toFixed()}%`}</S.ProgressBarPercentage>
          <S.ProgressBarContent>
            <S.ProgressBarContentMain percentage={percentage} />
          </S.ProgressBarContent>
        </S.ProgressBarContainer>
      );
    },
    [],
  );

  return (
    <CustomCard title={t('consumption')} bodyStyle={{ padding: 24 }}>
      <Row gutter={[16, 16]}>
        {hasCurrentConsumption && (
          <S.ColCurrentConsumption md={8} sm={24} xs={24}>
            <Col key="currentConsumption" span={24}>
              <S.ConsumptionSectionHeader>
                <S.ConsumptionSectionHeaderTitle>
                  {t('currentConsumption') as string}
                  {statsInfo('currentConsumptionInfo')}
                </S.ConsumptionSectionHeaderTitle>
              </S.ConsumptionSectionHeader>
              <S.ConsumptionStat
                title={`${t('totalConsumption')} / ${t('totalFranchise')}`}
                value={`${bytesFormat(
                  currentConsumption?.consumption,
                )} / ${bytesFormat(currentConsumption?.franchise)}`}
              />
            </Col>
            {progressBar(
              currentConsumption?.consumption,
              currentConsumption?.franchise,
              300,
            )}
          </S.ColCurrentConsumption>
        )}
        <S.ColRateConsumption
          md={hasCurrentConsumption ? 16 : 24}
          sm={24}
          xs={24}
          hasCurrentConsumption={hasCurrentConsumption}
        >
          {hasCurrentConsumption && (
            <S.ConsumptionSectionHeader>
              <S.ConsumptionSectionHeaderTitle>
                {t('rateConsumption') as string}
                {statsInfo('rateConsumptionInfo')}
              </S.ConsumptionSectionHeaderTitle>
              <S.ConsumptionSectionHeaderDate>
                <span>{`${t('updatedIn')}: `}</span>
                {updatedDate}
              </S.ConsumptionSectionHeaderDate>
            </S.ConsumptionSectionHeader>
          )}
          <Row gutter={[16, 16]}>
            {rateConsumptionStates?.map(({ name, title, value, md }) => (
              <Col key={name} md={md} sm={24} xs={24}>
                <S.ConsumptionStat
                  title={title ?? t(name)}
                  type={name as S.StatType}
                  value={value}
                />
              </Col>
            ))}
          </Row>
          {progressBar(data?.totalConsumption, data?.totalFranchise, 500)}
        </S.ColRateConsumption>
      </Row>
    </CustomCard>
  );
};

export default ConsumptionCard;
